import { useAuth } from 'auth/AuthProvider';
import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export interface OrgContextValue {
	tenantID: string | null;
	tenant?: { id: string; name: string; hostname?: string | null; workspaces: { id: string; name: string }[] };
	workspaceID: string | null;
	workspace?: { id: string; name: string };
	changeContext: (tenantID: string | null, workspaceID: string | null) => void;
	isInTenant: boolean;
	isInWorkspace: boolean;
	isInSystem: boolean;
	reload?: () => void;
	availableTenants?: { id: string; name: string; hostname?: string | null; workspaces: { id: string; name: string }[] }[];
	apiBase?: string;
	apiBaseTenant?: string | null;
	apiBaseWorkspace?: string | null;
	apiBaseSettings?: string;
	contextPath?: string;
	tenantContextPath?: string;
	appPath?: string;
}

const OrgContextReactContext = React.createContext<OrgContextValue | null>(null);

export function useOrgContext() {
	return React.useContext(OrgContextReactContext);
}

/**
 * Guard that ensures a tenantID is present (either from URL params or hostname-matched context).
 */
export const RequireTenant = ({ children }: { children: React.ReactNode }) => {
	const { tenantID: paramTenantID } = useParams();
	const ctx = useOrgContext();

	if (!paramTenantID && !ctx?.tenantID) return <p>Select a tenant from the selector above.</p>;

	return <>{children}</>;
};

/**
 * Guard that ensures both tenantID and workspaceID are present (either from URL params or context).
 */
export const RequireTenantWorkspace = ({ children }: { children: React.ReactNode }) => {
	const { tenantID: paramTenantID, workspaceID: paramWorkspaceID } = useParams();
	const ctx = useOrgContext();

	const effectiveTenantID = paramTenantID || ctx?.tenantID;
	const effectiveWorkspaceID = paramWorkspaceID || ctx?.workspaceID;

	if (!effectiveTenantID || !effectiveWorkspaceID) return <p>Select a tenant and workspace from the selector above.</p>;

	return <>{children}</>;
};

/**
 * A wrapper around useNavigate that automatically prefixes the current tenant/workspace context to the navigation path.
 * Uses context-derived paths, so it works correctly for both standard and hostname-based URLs.
 */
export const useOrgNavigate = () => {
	const navigate = useNavigate();
	const ctx = useOrgContext();
	return (to: string, options?: { replace?: boolean; state?: any; level?: 'system' | 'tenant' | 'workspace' }) => {
		let effectiveContextPath: string;

		const level = options?.level || 'workspace';

		if (level === 'system') effectiveContextPath = '';
		else if (level === 'tenant') effectiveContextPath = ctx?.tenantContextPath ?? '';
		else if (level === 'workspace') effectiveContextPath = ctx?.contextPath ?? '';
		else effectiveContextPath = '';

		navigate(`${effectiveContextPath}${to}`, options);
	};
};

/**
 * A wrapper around Link that automatically prefixes the current tenant/workspace context to the "to" path.
 * Uses context-derived paths, so it works correctly for both standard and hostname-based URLs.
 */
export const OrgContextLink = ({ to, level, children }: { to: string; level?: 'system' | 'tenant' | 'workspace'; children: React.ReactNode }) => {
	const ctx = useOrgContext();

	level ||= 'workspace';

	let effectiveContextPath: string;

	if (level === 'system') effectiveContextPath = '';
	else if (level === 'tenant') effectiveContextPath = ctx?.tenantContextPath ?? '';
	else if (level === 'workspace') effectiveContextPath = ctx?.contextPath ?? '';
	else effectiveContextPath = '';

	return (
		<Link to={`${effectiveContextPath}${to}`}>
			{children}
		</Link>
	);
}

const OrgContextProvider = ({ children }: { children: React.ReactNode }) => {
	const auth = useAuth();
	const navigate = useNavigate();

	const availableTenants = auth?.profile?.tenants.map(t => ({
		id: t.id,
		name: t.name,
		hostname: t.hostname ?? null,
		workspaces: t.workspaces?.map(w => ({ id: w.id, name: w.name })) ?? []
	})) ?? [];

	// Detect if we're on a tenant hostname
	const currentHostname = window.location.hostname;
	const hostnameMatchedTenant = availableTenants.find(t => t.hostname && t.hostname === currentHostname) ?? null;
	const onTenantHostname = hostnameMatchedTenant !== null;

	const rawParts = window.location.pathname.split('/');
	const pathParts = rawParts.filter(part => part !== '');

	let tenantID: string | null;
	let workspaceID: string | null;
	let isInWorkspace: boolean;
	let isInTenant: boolean;

	if (onTenantHostname && hostnameMatchedTenant) {
		// Tenant is implied by the hostname; workspace comes from /w/:workspaceID at the root
		tenantID = hostnameMatchedTenant.id;
		workspaceID = pathParts[0] === 'w' ? (pathParts[1] ?? null) : null;
		isInWorkspace = pathParts[0] === 'w';
		isInTenant = true;
	} else {
		tenantID = pathParts[0] === 't' ? (pathParts[1] ?? null) : null;
		workspaceID = pathParts[0] === 't' && pathParts[2] === 'w' ? (pathParts[3] ?? null) : null;
		isInWorkspace = pathParts[0] === 't' && pathParts[2] === 'w';
		isInTenant = pathParts[0] === 't';
	}

	const isInSystem = !isInTenant && !isInWorkspace;

	// tenantContextPath: URL prefix for tenant-level navigation (empty when hostname implies the tenant)
	const tenantContextPath = onTenantHostname ? '' : (tenantID ? `/t/${tenantID}` : '');

	// contextPath: URL prefix for workspace-level navigation
	const contextPath = onTenantHostname
		? (workspaceID ? `/w/${workspaceID}` : '')
		: (tenantID && workspaceID ? `/t/${tenantID}/w/${workspaceID}` : tenantID ? `/t/${tenantID}` : '');

	// appPath: the app-relative portion of the current URL (without context prefix)
	const appPath = '/' + (() => {
		if (onTenantHostname) {
			return workspaceID ? rawParts.slice(3).join('/') : rawParts.slice(1).join('/');
		} else {
			return tenantID && workspaceID ? rawParts.slice(5).join('/')
				: tenantID ? rawParts.slice(3).join('/')
				: rawParts.slice(1).join('/');
		}
	})();

	// API base paths — use short form (/api/w/:id) when on a tenant hostname
	const apiBase = onTenantHostname
		? (workspaceID ? `/api/w/${workspaceID}` : '/api')
		: (tenantID && workspaceID ? `/api/t/${tenantID}/w/${workspaceID}` : tenantID ? `/api/t/${tenantID}` : '/api');

	const apiBaseTenant = onTenantHostname
		? '/api'
		: (tenantID ? `/api/t/${tenantID}` : null);

	const apiBaseWorkspace = onTenantHostname
		? (workspaceID ? `/api/w/${workspaceID}` : null)
		: (tenantID && workspaceID ? `/api/t/${tenantID}/w/${workspaceID}` : null);

	const apiBaseSettings = onTenantHostname
		? (workspaceID ? `/api/w/${workspaceID}/settings` : '/api/settings')
		: (tenantID && workspaceID ? `/api/t/${tenantID}/w/${workspaceID}/settings` : tenantID ? `/api/t/${tenantID}/settings` : '/api/settings');

	const changeContext = (newTenantID: string | null, newWorkspaceID: string | null) => {
		if (newTenantID !== null && !availableTenants.some(t => t.id === newTenantID)) {
			console.warn(`Attempted to change to tenantID ${newTenantID} which is not in availableTenants`);
			return;
		}

		const targetTenantWorkspaces = newTenantID ? (availableTenants.find(t => t.id === newTenantID)?.workspaces ?? []) : [];
		if (newWorkspaceID && !targetTenantWorkspaces.some(w => w.id === newWorkspaceID)) {
			// Requested workspace doesn't belong to the target tenant — fall back to first available
			newWorkspaceID = targetTenantWorkspaces[0]?.id || null;
		} else if (tenantID !== newTenantID && !newWorkspaceID && isInWorkspace) {
			// Currently in a workspace — try to stay in one on the target tenant
			newWorkspaceID = targetTenantWorkspaces[0]?.id || null;
		}

		// System admins are exempt from hostname-based navigation — they can freely switch between
		// tenants from any hostname (mirroring the backend exemption in resolveContext).
		const isSystemAdmin = availableTenants.some(t => t.id === 'system');

		// Determine target hostname info
		const targetTenant = newTenantID ? availableTenants.find(t => t.id === newTenantID) : null;
		const targetHostname = targetTenant?.hostname || null;

		// Use short /w/:id URL form only when the user will actually land on the target tenant's hostname.
		// System admins stay on the current hostname, so only use short form if the target hostname
		// happens to be the one they're already on.
		const willBeOnTargetHostname = !!targetHostname && (!isSystemAdmin || targetHostname === currentHostname);
		const targetPath = willBeOnTargetHostname
			? (newWorkspaceID ? `/w/${newWorkspaceID}${appPath}` : appPath)
			: (newTenantID && newWorkspaceID
				? `/t/${newTenantID}/w/${newWorkspaceID}${appPath}`
				: newTenantID
					? `/t/${newTenantID}${appPath}`
					: appPath);

		// Determine whether a cross-hostname navigation is required
		const onTenantHostnameNow = availableTenants.some(t => t.hostname && t.hostname === currentHostname);

		if (!isSystemAdmin && targetHostname && targetHostname !== currentHostname) {
			// Target tenant has a different hostname — navigate to it
			window.location.href = `https://${targetHostname}${targetPath}`;
		} else if (!isSystemAdmin && !targetHostname && onTenantHostnameNow) {
			// Target has no hostname but we're on a tenant hostname — go to the system base URL
			const systemBaseUrl = auth?.profile?.systemBaseUrl;
			if (systemBaseUrl) {
				window.location.href = `${systemBaseUrl.replace(/\/$/, '')}${targetPath}`;
			} else {
				navigate(targetPath);
			}
		} else {
			navigate(targetPath);
		}
	};

	const reload = () => {
		auth?.reloadProfile();
	};

	useEffect(() => {
		// Reload the auth profile on mount to ensure we have the latest tenant/workspace access info, and to trigger a re-render of the context with the correct availableTenants and current tenant/workspace.
		reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const value: OrgContextValue = {
		tenantID,
		tenant: tenantID ? availableTenants.find(t => t.id === tenantID) : undefined,
		workspaceID,
		workspace: workspaceID && tenantID ? availableTenants.find(t => t.id === tenantID)?.workspaces?.find(w => w.id === workspaceID) : undefined,
		changeContext,
		availableTenants,
		isInWorkspace,
		isInTenant,
		isInSystem,
		contextPath,
		tenantContextPath,
		appPath,
		apiBase,
		apiBaseTenant,
		apiBaseWorkspace,
		apiBaseSettings,
		reload
	};

	return (
		<OrgContextReactContext.Provider value={value}>
			{children}
		</OrgContextReactContext.Provider>
	);
};

export default OrgContextProvider;
